import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  FileText, CheckCircle, Clock, XCircle, ExternalLink, ShieldCheck, Filter,
  Download, Square, CheckSquare, UserCheck, AlertTriangle, Undo2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import PageLayout from '../components/PageLayout';

const LICENSE_TYPE_LABELS = {
  medical_license: 'Medical License',
  dea_certificate: 'DEA Certificate',
  board_certification: 'Board Certification',
  anesthesia_certification: 'Anesthesia Certification',
  other: 'Other Credential',
};

const StatusBadge = ({ status }) => {
  if (status === 'verified') return (
    <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
      <CheckCircle className="w-3 h-3" /> Verified
    </Badge>
  );
  if (status === 'rejected') return (
    <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
      <XCircle className="w-3 h-3" /> Rejected
    </Badge>
  );
  return (
    <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 gap-1">
      <Clock className="w-3 h-3" /> Pending
    </Badge>
  );
};

const LicenseManagementPage = () => {
  const { logout } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [reviewState, setReviewState] = useState({}); // licenseId -> {notes, status}
  const [externalNotes, setExternalNotes] = useState({}); // user_id -> notes
  const [externalSubmitting, setExternalSubmitting] = useState({}); // user_id -> bool

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [licRes, missRes] = await Promise.all([
        api.get('/admin/licenses'),
        api.get('/admin/licenses/missing').catch(() => ({ data: [] })),
      ]);
      setLicenses(licRes.data);
      setMissing(missRes.data || []);
    } catch {
      toast.error('Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (licenseId, status) => {
    const notes = reviewState[licenseId]?.notes || null;
    try {
      const params = new URLSearchParams({ status });
      if (notes) params.append('notes', notes);
      await api.put(`/licenses/${licenseId}/status?${params.toString()}`);
      toast.success(`License ${status}`);
      fetchAll();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleToggleRegistryVerified = async (licenseId) => {
    try {
      await api.put(`/licenses/${licenseId}/registry-verified`);
      fetchAll();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleExternalVerify = async (userId, licenseType = 'medical_license') => {
    setExternalSubmitting(prev => ({ ...prev, [userId]: true }));
    try {
      await api.post('/admin/licenses/external-verify', {
        user_id: userId,
        license_type: licenseType,
        notes: externalNotes[userId] || null,
      });
      toast.success('Marked as externally verified — participant emailed');
      setExternalNotes(prev => { const n = { ...prev }; delete n[userId]; return n; });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark externally verified');
    } finally {
      setExternalSubmitting(prev => { const n = { ...prev }; delete n[userId]; return n; });
    }
  };

  const handleUndoExternal = async (licenseId) => {
    if (!window.confirm('Remove this external verification? The participant will need to upload or be re-verified.')) return;
    try {
      await api.delete(`/admin/licenses/${licenseId}/external`);
      toast.success('External verification removed');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const filtered = filterStatus === 'all'
    ? licenses
    : filterStatus === 'missing'
      ? []
      : licenses.filter(l => l.status === filterStatus);

  const counts = {
    total: licenses.length,
    pending: licenses.filter(l => l.status === 'pending').length,
    verified: licenses.filter(l => l.status === 'verified').length,
    rejected: licenses.filter(l => l.status === 'rejected').length,
    registryVerified: licenses.filter(l => l.status === 'verified' && l.registry_verified).length,
    missing: missing.length,
  };

  const filterAction = (
    <Select value={filterStatus} onValueChange={setFilterStatus}>
      <SelectTrigger className="w-44" data-testid="filter-status">
        <Filter className="w-4 h-4 mr-2" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All ({counts.total})</SelectItem>
        <SelectItem value="pending">Pending ({counts.pending})</SelectItem>
        <SelectItem value="verified">Verified ({counts.verified})</SelectItem>
        <SelectItem value="rejected">Rejected ({counts.rejected})</SelectItem>
        <SelectItem value="missing">Missing Upload ({counts.missing})</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <PageLayout title="License & Credential Review" headerActions={filterAction} maxWidth="max-w-5xl">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
            <Card className="text-center py-4">
              <p className="text-2xl font-bold text-yellow-600">{counts.pending}</p>
              <p className="text-sm text-slate-500">Pending Review</p>
            </Card>
            <Card className="text-center py-4">
              <p className="text-2xl font-bold text-green-600">{counts.verified}</p>
              <p className="text-sm text-slate-500">Approved</p>
            </Card>
            <Card className="text-center py-4">
              <p className="text-2xl font-bold text-primary">{counts.registryVerified}/{counts.verified}</p>
              <p className="text-sm text-slate-500">Registry Verified</p>
            </Card>
            <Card className="text-center py-4">
              <p className="text-2xl font-bold text-red-500">{counts.rejected}</p>
              <p className="text-sm text-slate-500">Rejected</p>
            </Card>
            <Card className="text-center py-4">
              <p className="text-2xl font-bold text-amber-600">{counts.missing}</p>
              <p className="text-sm text-slate-500">Missing Upload</p>
            </Card>
          </div>

          {/* ── Missing Uploads tab ── */}
          {filterStatus === 'missing' && (
            loading ? (
              <div className="text-center py-16 text-slate-400">Loading…</div>
            ) : missing.length === 0 ? (
              <div className="text-center py-16">
                <ShieldCheck className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">Everyone who needs a credential is on file</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-slate-500 mb-2">
                  Participants whose role requires a license and who have not uploaded one. Use <strong>Mark Externally Verified</strong> if you confirmed their license through an external registry — no file needed. They'll receive the standard credential-approved email.
                </p>
                {missing.map(row => {
                  const uid = row.user_id;
                  const name = `${row.profile?.first_name || ''} ${row.profile?.last_name || ''}`.trim() || row.user?.name || 'Unknown';
                  const roles = row.profile?.work_roles || [];
                  const submitting = !!externalSubmitting[uid];
                  return (
                    <Card key={uid} className="overflow-hidden" data-testid={`missing-license-${uid}`}>
                      <CardContent className="pt-5 pb-5">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <AlertTriangle className="w-5 h-5 text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <p className="font-semibold text-slate-800 text-sm">{name}</p>
                                <span className="text-slate-400 text-xs">{row.user?.email}</span>
                                {roles.length > 0 && (
                                  <Badge variant="outline" className="text-xs">{roles.join(', ')}</Badge>
                                )}
                              </div>
                              <p className="text-xs text-slate-500">
                                {row.trips?.length > 0
                                  ? `Trips: ${row.trips.map(t => t.name).join(', ')}`
                                  : 'No active trip'}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col sm:items-end gap-2 flex-shrink-0 w-full sm:w-auto">
                            <Textarea
                              placeholder="Optional note (e.g., registry name, verification date)…"
                              className="text-sm resize-none h-14 w-full sm:w-72"
                              value={externalNotes[uid] || ''}
                              onChange={e => setExternalNotes(prev => ({ ...prev, [uid]: e.target.value }))}
                              data-testid={`external-notes-${uid}`}
                            />
                            <Button
                              size="sm"
                              className="bg-primary hover:bg-primary/90 text-white h-8"
                              disabled={submitting}
                              onClick={() => handleExternalVerify(uid)}
                              data-testid={`external-verify-${uid}`}
                            >
                              <UserCheck className="w-3.5 h-3.5 mr-1" />
                              {submitting ? 'Marking…' : 'Mark Externally Verified'}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )
          )}

          {/* ── Existing license list (all other filters) ── */}
          {filterStatus !== 'missing' && (loading ? (
            <div className="text-center py-16 text-slate-400">Loading licenses...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <ShieldCheck className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No credentials found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map((lic) => (
                <Card key={lic.license_id} className="overflow-hidden" data-testid={`admin-license-${lic.license_id}`}>
                  <CardContent className="pt-5 pb-5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      {/* Left: file info */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${lic.externally_verified ? 'bg-emerald-50' : 'bg-primary/10'}`}>
                          {lic.externally_verified
                            ? <UserCheck className="w-5 h-5 text-emerald-600" />
                            : <FileText className="w-5 h-5 text-primary" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-semibold text-slate-800 text-sm">
                              {lic.user?.name || 'Unknown User'}
                            </p>
                            <span className="text-slate-400 text-xs">{lic.user?.email}</span>
                            {lic.profile?.work_roles?.length > 0 && (
                              <Badge variant="outline" className="text-xs">{lic.profile.work_roles.join(', ')}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">
                            {lic.externally_verified ? <span className="italic text-emerald-700">External registry verification (no file uploaded)</span> : lic.file_name}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {LICENSE_TYPE_LABELS[lic.license_type] || lic.license_type}
                            {' · '}
                            {lic.externally_verified ? 'Verified' : 'Uploaded'} {new Date(lic.uploaded_at).toLocaleDateString()}
                            {lic.trip_id && ` · Trip: ${lic.trip_id}`}
                          </p>
                          {lic.notes && (
                            <p className="text-xs text-slate-500 mt-1 italic">Admin note: {lic.notes}</p>
                          )}
                        </div>
                      </div>

                      {/* Right: status + actions */}
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          <StatusBadge status={lic.status} />
                          {lic.externally_verified && (
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
                              <UserCheck className="w-3 h-3" /> External
                            </Badge>
                          )}
                        </div>
                        {!lic.externally_verified && (
                          <div className="flex items-center gap-1.5">
                            <a
                              href={`${BACKEND_URL}/api/licenses/${lic.license_id}/file`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`view-license-${lic.license_id}`}
                            >
                              <Button size="sm" variant="outline" className="text-xs h-7">
                                <ExternalLink className="w-3 h-3 mr-1" /> View
                              </Button>
                            </a>
                            <a
                              href={`${BACKEND_URL}/api/licenses/${lic.license_id}/file?download=true`}
                              download
                              data-testid={`download-license-${lic.license_id}`}
                            >
                              <Button size="sm" variant="outline" className="text-xs h-7">
                                <Download className="w-3 h-3 mr-1" /> Download
                              </Button>
                            </a>
                          </div>
                        )}
                        {lic.status === 'verified' && !lic.externally_verified && (
                          <button
                            onClick={() => handleToggleRegistryVerified(lic.license_id)}
                            className={`flex items-center gap-1.5 text-xs mt-1 px-2 py-1 rounded-lg transition-colors ${
                              lic.registry_verified
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                            data-testid={`registry-verify-${lic.license_id}`}
                          >
                            {lic.registry_verified
                              ? <CheckSquare className="w-3.5 h-3.5" />
                              : <Square className="w-3.5 h-3.5" />}
                            {lic.registry_verified ? 'Verified' : 'Mark Verified'}
                          </button>
                        )}
                        {lic.externally_verified && (
                          <button
                            onClick={() => handleUndoExternal(lic.license_id)}
                            className="flex items-center gap-1.5 text-xs mt-1 px-2 py-1 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                            data-testid={`undo-external-${lic.license_id}`}
                          >
                            <Undo2 className="w-3.5 h-3.5" /> Undo External Verify
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Review actions (shown for pending items) */}
                    {lic.status === 'pending' && (
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-end gap-3">
                        <div className="flex-1">
                          <Textarea
                            placeholder="Optional review notes..."
                            className="text-sm resize-none h-16"
                            value={reviewState[lic.license_id]?.notes || ''}
                            onChange={(e) => setReviewState(prev => ({
                              ...prev,
                              [lic.license_id]: { notes: e.target.value }
                            }))}
                            data-testid={`review-notes-${lic.license_id}`}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white h-8"
                            onClick={() => handleStatusUpdate(lic.license_id, 'verified')}
                            data-testid={`verify-license-${lic.license_id}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" /> Verify
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50 h-8"
                            onClick={() => handleStatusUpdate(lic.license_id, 'rejected')}
                            data-testid={`reject-license-${lic.license_id}`}
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Re-review actions for already reviewed items (skip external — use Undo instead) */}
                    {lic.status !== 'pending' && !lic.externally_verified && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs text-slate-500 h-7"
                          onClick={() => handleStatusUpdate(lic.license_id, 'pending')}
                          data-testid={`reset-license-${lic.license_id}`}
                        >
                          Reset to Pending
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ))}
    </PageLayout>
  );
};

export default LicenseManagementPage;
